import React, { FC, Suspense, useEffect, useState } from 'react'
import cn from 'classnames'
import { DivProps } from '@commonTypes'
import { ThemesProvider } from '@providers/themesProvider'
import { Router, navigate } from '@reach/router'
import { ApolloProvider } from 'react-apollo'
import { withLDConsumer } from 'launchdarkly-react-client-sdk'
import { LDFlagSet, LDUser } from 'launchdarkly-js-client-sdk'
import { accessToken } from '@stores/apollo'
import { Flag, ICurrentUser, Role } from '@api'
import { UserContextProvider, useUser, ReloadContextProvider } from '@hooks'
import { MapContextProvider } from '@providers/mapProvider'
import { Icon, Logo } from '@ui'
import FloorPlan from './components/AdminFloorPlan'
import { UnauthorizedApp } from 'components/UnauthorizedApp'
import Navigation from 'components/Navigation'
import UnderActiveConstruction from 'components/UnderActiveConstruction'
import Dashboard from 'components/Dashboard'
import Portfolio from 'components/Portfolio'
import PortfolioDetails from 'components/Portfolio/Details'
import EmployeeManagement from 'components/EmployeeManagement'
import EmployeeImportHistory from 'components/EmployeeManagement/ImportHistoryTable'
import ImportDetails from 'components/EmployeeManagement/ImportDetails'
import LeaseManagement from 'components/LeaseManagement'
import TicketManagement from 'components/TicketManagement'
import ReportsCatalog from 'components/Reports/ReportsCatalog'
import Cost from 'components/Cost'
import Integrations from 'components/Integrations'
import ReportPage from 'components/Reports/ReportPage'
import Sisense from 'components/Sisense'
import BuildingManager from 'components/BuildingManagement'
import apolloClient from '@stores/apollo'
import Stack from 'components/Stack'
import Projections from 'components/Projections/index'
import PortfolioProjections from 'components/PortfolioProjections'
import Playground from 'components/Playground'
import AssetManagement from 'components/AssetManagement'
import WorkplaceGroupManagement from 'components/WorkplaceGroupManagement'
import NeighborhoodManagement from 'components/NeighborhoodManagement'
import NeighborhoodImportDetails from 'components/NeighborhoodManagement/NeighborhoodImport/ImportDetails'
import NeighborhoodImportHistory from 'components/NeighborhoodManagement/NeighborhoodImport/ImportHistory'
import DepartmentManagement from 'components/DepartmentManagement'
import BookingsManagement from 'components/BookingsManagement'
import BookingImportHistory from 'components/BookingsManagement/ImportHistory'
import BookingImportDetails from 'components/BookingsManagement/ImportDetails'
import BuildingTypeManagement from 'components/BuildingTypeManagement'
import SpaceTypeManagement from 'components/SpaceTypeManagement'
import Settings from 'components/NewSettings'
import { AppDataProvider, BookingDeskProvider } from '@providers'
import { printMapPath, teamsBookingsPath } from '@utils'
import { reloadAphrodite } from '@utils/navigateToAphrodite'
import { LDProps } from 'launchdarkly-react-client-sdk/lib/withLDConsumer'
import css from './appRoot.module.scss'
import PrintMap from './components/PrintMap/PrintMap'
import BookingsTeamsTab from './components/BookingsTeamsTab/BookingsTeamsTab'
import MoveOrders from 'components/MoveOrders'
import GlobalMap from 'components/GlobalMap'
import { SplitIOProvider } from '@providers/splitIOProvider'

declare global {
  interface Window {
    enableSegmentTest: () => void
    disableSegmentTest: () => void
    graphqlEndpoint: string
    initSegment: (s: string) => void
    getSelection: () => Selection | null
    IntersectionObserver: any
    localStorage: {
      getItem: (key: string) => string
      setItem: (key: string, value: string) => void
    }
    analytics?: {
      identify: (userId: string, extraAttributes: any) => void
    }
  }
}

const SEGMENT = 'SEGMENT_TEST'

window.enableSegmentTest = () => {
  localStorage.setItem(SEGMENT, '1')
  console.log(
    '%c Segment Test enabled for apollo ',
    'background: cornflowerblue; color: white; border-radius: 2px; border: 1px solid rgba(0,0,0,0.1);'
  )
}

window.disableSegmentTest = () => {
  localStorage.removeItem(SEGMENT)
  console.log(
    '%c Segment Test disabled for apollo ',
    'background: tomato; color: white; border-radius: 2px; border: 1px solid rgba(0,0,0,0.1);'
  )
}

// ============= APHRODITE COMPATIBILITY ==============
// Is the app being run inside of an iframe?
// If so, hide navigation, etc..
export let legacyMode = window.self !== window.top
export const themeMode = process.env.REACT_APP_THEME_SUPPORT === 'true'
// Navigate with the basepath prepended so all navigation
// in aphrodite works properly.
export const navigateWithBasepath = (to: string, opts: any) => {
  navigate(`${process.env.PUBLIC_URL}${to}`, opts)
}

// INIT SEGMENT
if (process.env.REACT_APP_APOLLO_SEGMENT && window.location.href.includes('main.spaceiq.com')) {
  window.initSegment(process.env.REACT_APP_APOLLO_SEGMENT)
} else if (process.env.REACT_APP_APOLLO_SEGMENT_TEST && localStorage.getItem(SEGMENT) === '1') {
  window.initSegment(process.env.REACT_APP_APOLLO_SEGMENT_TEST)
} else {
  console.warn(
    '%c Apollo Segment is not enabled! ',
    'background: gold; color: black; border-radius: 2px; border: 1px solid rgba(0,0,0,0.1);'
  )
}

const AllRoles = [
  Role.ADMIN,
  Role.LOCATION_ADMIN,
  Role.POWER_ADMIN,
  Role.SPACE_CAPTAIN,
  Role.DEPARTMENT_ADMIN,
  Role.VIEWER,
  Role.IT,
  Role.BOOKINGS_MANAGER,
]

const routes: Route[] = [
  {
    props: { path: 'dashboard' },
    View: Dashboard,
    permitted: [Role.ADMIN, Role.POWER_ADMIN],
  },
  {
    props: { path: '/' },
    View: FloorPlan,
    permitted: AllRoles,
  },
  {
    props: { path: 'floor_plan' },
    View: FloorPlan,
    permitted: AllRoles,
    // flags: [Flag.FLOOR_PLAN],
  },
  {
    props: { path: 'floor_plan/:floorId' },
    View: FloorPlan,
    permitted: AllRoles,
  },
  {
    props: { path: 'floor_plan/:floorId/ap/:apId' },
    View: FloorPlan,
    permitted: AllRoles,
  },
  {
    props: { path: 'finder' },
    View: FloorPlan,
    permitted: [Role.ADMIN, Role.BOOKINGS_MANAGER, Role.IT, Role.POWER_ADMIN],
  },
  {
    props: { path: 'finder/floor_plan' },
    View: FloorPlan,
    permitted: [Role.ADMIN, Role.BOOKINGS_MANAGER, Role.IT, Role.POWER_ADMIN],
  },
  {
    props: { path: 'finder/floor_plan/:floorId' },
    View: FloorPlan,
    permitted: [Role.ADMIN, Role.BOOKINGS_MANAGER, Role.IT, Role.POWER_ADMIN],
  },
  {
    props: { path: 'global' },
    View: GlobalMap,
    permitted: [Role.ADMIN, Role.LOCATION_ADMIN, Role.POWER_ADMIN, Role.SPACE_CAPTAIN],
  },
  {
    props: { path: 'floor_plan/:floorId/space/:spaceId' },
    View: FloorPlan,
    permitted: AllRoles,
  },
  {
    props: { path: 'portfolio' },
    View: Portfolio,
    permitted: [Role.ADMIN, Role.LOCATION_ADMIN, Role.POWER_ADMIN, Role.SPACE_CAPTAIN],
  },
  {
    props: { path: 'portfolio/:buildingId' },
    View: PortfolioDetails,
    permitted: [Role.ADMIN, Role.LOCATION_ADMIN, Role.POWER_ADMIN, Role.SPACE_CAPTAIN],
  },
  {
    props: { path: 'portfolio/:buildingId/:floorId' },
    View: PortfolioDetails,
    permitted: [Role.ADMIN, Role.LOCATION_ADMIN, Role.POWER_ADMIN, Role.SPACE_CAPTAIN],
  },
  {
    props: { path: 'stack' },
    View: Stack,
    permitted: [Role.ADMIN, Role.POWER_ADMIN],
  },
  {
    props: { path: 'stack/:cityName/:buildingId' },
    View: Stack,
    permitted: [Role.ADMIN, Role.POWER_ADMIN],
  },
  {
    props: { path: 'stack/:cityName/:buildingId/:floorId' },
    View: Stack,
    permitted: [Role.ADMIN, Role.POWER_ADMIN],
  },
  {
    props: { path: 'move_orders' },
    View: MoveOrders,
    permitted: [
      Role.ADMIN,
      Role.LOCATION_ADMIN,
      Role.POWER_ADMIN,
      Role.SPACE_CAPTAIN,
      Role.DEPARTMENT_ADMIN,
    ],
  },

  {
    props: { path: 'reports/' },
    View: ReportsCatalog,
    permitted: [Role.ADMIN, Role.LOCATION_ADMIN, Role.POWER_ADMIN, Role.SPACE_CAPTAIN],
  },
  {
    props: { path: 'reports/:categories' },
    View: ReportsCatalog,
    permitted: [Role.ADMIN, Role.LOCATION_ADMIN, Role.POWER_ADMIN, Role.SPACE_CAPTAIN],
  },
  {
    props: { path: 'reports/:categories/:id' },
    View: ReportPage,
    permitted: [Role.ADMIN, Role.LOCATION_ADMIN, Role.POWER_ADMIN, Role.SPACE_CAPTAIN],
  },
  {
    props: { path: 'reports/:id/:spaceId' },
    View: ReportPage,
    permitted: [Role.ADMIN, Role.LOCATION_ADMIN, Role.POWER_ADMIN, Role.SPACE_CAPTAIN],
  },
  {
    props: { path: 'sisense' },
    View: Sisense,
    permitted: [Role.ADMIN, Role.LOCATION_ADMIN, Role.POWER_ADMIN, Role.SPACE_CAPTAIN],
    flags: [Flag.SISENSE],
  },
  {
    props: { path: 'projections_imports' },
    View: Projections,
    permitted: [Role.ADMIN, Role.POWER_ADMIN],
  },
  {
    props: { path: 'projections' },
    View: PortfolioProjections,
    permitted: [Role.ADMIN, Role.POWER_ADMIN],
  },
  {
    props: { path: 'cost' },
    View: Cost,
    permitted: [Role.ADMIN, Role.POWER_ADMIN],
  },
  {
    props: { path: 'employees' },
    View: EmployeeManagement,
    permitted: [Role.ADMIN, Role.LOCATION_ADMIN, Role.POWER_ADMIN, Role.SPACE_CAPTAIN],
  },
  {
    props: { path: 'employees/history' },
    View: EmployeeImportHistory,
    permitted: [Role.ADMIN, Role.POWER_ADMIN],
  },
  {
    props: { path: 'employees/history/:id' },
    View: ImportDetails,
    permitted: [Role.ADMIN, Role.POWER_ADMIN],
  },
  {
    props: { path: 'leases/*' },
    View: LeaseManagement,
    permitted: [Role.ADMIN, Role.POWER_ADMIN],
  },
  {
    props: { path: 'tickets/*' },
    View: TicketManagement,
    permitted: [Role.ADMIN, Role.LOCATION_ADMIN, Role.POWER_ADMIN, Role.SPACE_CAPTAIN],
  },
  {
    props: { path: 'playground' },
    View: Playground,
    permitted: [
      Role.ADMIN,
      Role.LOCATION_ADMIN,
      Role.POWER_ADMIN,
      Role.SPACE_CAPTAIN,
      Role.DEPARTMENT_ADMIN,
      Role.IT,
    ],
  },
  {
    props: { path: 'departments' },
    View: DepartmentManagement,
    permitted: [Role.ADMIN, Role.POWER_ADMIN],
  },
  {
    props: { path: 'integrations' },
    View: Integrations,
    permitted: [Role.ADMIN, Role.LOCATION_ADMIN, Role.IT],
  },
  {
    props: { path: 'integrations/:id' },
    View: Integrations,
    permitted: [Role.ADMIN, Role.LOCATION_ADMIN, Role.IT],
  },
  {
    props: { path: 'buildingManager/*' },
    View: BuildingManager,
    permitted: [Role.ADMIN, Role.LOCATION_ADMIN, Role.POWER_ADMIN],
  },
  {
    props: { path: 'neighborhoods' },
    View: NeighborhoodManagement,
    permitted: [
      Role.ADMIN,
      Role.LOCATION_ADMIN,
      Role.POWER_ADMIN,
      Role.SPACE_CAPTAIN,
      Role.DEPARTMENT_ADMIN,
    ],
  },
  {
    props: { path: 'neighborhoods/history' },
    View: NeighborhoodImportHistory,
    permitted: [
      Role.ADMIN,
      Role.LOCATION_ADMIN,
      Role.POWER_ADMIN,
      Role.SPACE_CAPTAIN,
      Role.DEPARTMENT_ADMIN,
    ],
  },
  {
    props: { path: 'neighborhoods/history/:id' },
    View: NeighborhoodImportDetails,
    permitted: [
      Role.ADMIN,
      Role.LOCATION_ADMIN,
      Role.POWER_ADMIN,
      Role.SPACE_CAPTAIN,
      Role.DEPARTMENT_ADMIN,
    ],
  },
  {
    props: { path: 'workplace_groups' },
    View: WorkplaceGroupManagement,
    permitted: [Role.ADMIN, Role.POWER_ADMIN],
  },
  {
    props: { path: 'pins' },
    View: UnderActiveConstruction,
    permitted: [Role.ADMIN],
  },
  {
    props: { path: 'company_settings' },
    View: Settings,
    permitted: AllRoles,
  },

  {
    props: { path: 'company_settings/:id' },
    View: Settings,
    permitted: AllRoles,
  },
  {
    props: { path: 'api_docs' },
    View: UnderActiveConstruction,
    permitted: [Role.ADMIN],
  },
  {
    props: { path: 'assets' },
    View: AssetManagement,
    permitted: [Role.ADMIN, Role.POWER_ADMIN, Role.LOCATION_ADMIN],
  },
  {
    props: { path: 'new_projections' },
    View: Playground,
    permitted: [Role.ADMIN, Role.POWER_ADMIN, Role.LOCATION_ADMIN],
  },
  {
    props: { path: 'bookings' },
    View: BookingsManagement,
    permitted: [
      Role.ADMIN,
      Role.POWER_ADMIN,
      Role.LOCATION_ADMIN,
      Role.BOOKINGS_MANAGER,
      Role.SPACE_CAPTAIN,
      Role.DEPARTMENT_ADMIN,
    ],
  },
  {
    props: { path: 'bookings/history' },
    View: BookingImportHistory,
    permitted: [
      Role.ADMIN,
      Role.POWER_ADMIN,
      Role.LOCATION_ADMIN,
      Role.SPACE_CAPTAIN,
      Role.DEPARTMENT_ADMIN,
      Role.BOOKINGS_MANAGER,
    ],
  },
  {
    props: { path: 'bookings/history/:id' },
    View: BookingImportDetails,
    permitted: [
      Role.ADMIN,
      Role.POWER_ADMIN,
      Role.LOCATION_ADMIN,
      Role.SPACE_CAPTAIN,
      Role.DEPARTMENT_ADMIN,
      Role.BOOKINGS_MANAGER,
    ],
  },
  {
    props: { path: 'buildingTypeManagement' },
    View: BuildingTypeManagement,
    permitted: [Role.ADMIN, Role.POWER_ADMIN],
  },
  {
    props: { path: 'spaceTypeManagement' },
    View: SpaceTypeManagement,
    permitted: [Role.ADMIN, Role.POWER_ADMIN],
  },
  {
    props: { path: printMapPath },
    View: PrintMap,
    permitted: AllRoles,
  },
  {
    props: { path: teamsBookingsPath },
    View: BookingsTeamsTab,
    permitted: AllRoles,
  },
]

export interface Route {
  props: {
    path: string
  }
  View: any
  permitted: Role[]
  flags?: Flag[]
}

export default function App() {
  if (accessToken) {
    const SpaceIQRouterWithLD = withLDConsumer()(SpaceIQRouter)

    return (
      <ReloadContextProvider>
        <UserContextProvider>
          <SplitIOProvider>
            <AppDataProvider>
              <ThemesProvider>
                <ApolloProvider client={apolloClient.gql}>
                  <MapContextProvider>
                    <BookingDeskProvider>
                      <div className={css.container}>
                        <SpaceIQRouterWithLD />
                      </div>
                    </BookingDeskProvider>
                  </MapContextProvider>
                </ApolloProvider>
              </ThemesProvider>
            </AppDataProvider>
          </SplitIOProvider>
        </UserContextProvider>
      </ReloadContextProvider>
    )
  }

  if (legacyMode) {
    reloadAphrodite()
    return null
  }

  return <UnauthorizedApp />
}

const getLegacyMode = (user: ICurrentUser | null) => {
  return window.self === window.top ? false : user?.isClassic ? true : false
}

function SpaceIQRouter(props: LDProps) {
  const { user } = useUser()
  legacyMode = getLegacyMode(user)
  useEffect(() => {
    async function identifyUser() {
      if (props.ldClient) {
        const ldUser: LDUser = user
          ? {
              key: user.company.uuid,
              name: user.company.name,
            }
          : { anonymous: true }

        await props.ldClient.identify(ldUser)
      }
    }

    identifyUser()
  }, [user, props.ldClient])

  if (user) return <AuthenticatedApp user={user} flags={props.flags} />
  return <div />
}

function AuthenticatedApp({ user, flags }: { user: ICurrentUser; flags?: LDFlagSet }) {
  if (window.analytics)
    window.analytics.identify(user.employee.id, {
      company: {
        id: `SIQ-${user.company.uuid}`,
        name: user.company.name,
      },
      companyName: user.company.name,
      role: user.employee.role,
    })

  const role = user.employee.role
  const isFlagEnabled = (route: Route) =>
    !route.flags || route.flags.every((flag: string) => flags?.[flag])
  const allowedRoutes = routes.filter((r) => r.permitted.includes(role) && isFlagEnabled(r))
  const [pinLeftNav, setPinLeftNav] = useState(false)
  const [hideScrollbar, setHideScrollbar] = useState(false)
  return (
    <>
      {!legacyMode && (
        <>
          <div className={css.logo_wrapper}>
            <span
              className={css.bar_icon}
              onClick={() => {
                setPinLeftNav(!pinLeftNav)
                setHideScrollbar(!hideScrollbar)
              }}>
              <Icon name="light fa-bars" light={true}></Icon>
            </span>
            <Logo />
          </div>
          <Navigation
            pinLeftNav={pinLeftNav}
            role={role}
            flags={flags}
            hideScrollbar={hideScrollbar}
            setHideScrollbar={setHideScrollbar}
          />
        </>
      )}
      <Suspense fallback={<div>Loading...</div>}>
        <Content legacyMode={legacyMode} pinLeftNav={pinLeftNav}>
          <Router basepath={process.env.PUBLIC_URL}>
            {allowedRoutes.map(({ props, View }) => (
              <View key={props.path} {...props} pinLeftNav={pinLeftNav} />
            ))}
          </Router>
        </Content>
      </Suspense>
    </>
  )
}
type ContentPropsT = DivProps & { legacyMode: boolean; pinLeftNav: boolean }

const Content: FC<React.PropsWithChildren<ContentPropsT>> = ({
  legacyMode,
  pinLeftNav,
  className,
  style,
  ...props
}) => {
  return (
    <div
      className={cn(css.content, className)}
      style={{
        marginLeft: legacyMode ? 0 : pinLeftNav ? 270 : 70,
        ...style,
      }}
      {...props}
    />
  )
}
